/**
 * postContext.js
 * ──────────────
 * One shape for "the post this article template is showing", read by the Post
 * blocks (Post Title, Post Meta, Post Feature Image, Post Excerpt).
 *
 * The shape is the Post List item (PublicPostListService::Item): websitePostTitle,
 * websitePostExcerpt, websitePostDate, websitePostFeatureImageUrl(/Large/Full),
 * featureImageCredit, websitePostUrl, authors[], categories[], plus readingTime.
 *
 *  - Public site: Website/ajax/getPage.php fills `props.post` on every Post block
 *    (TemplateResolverService::FillPostBlocks) before the page reaches the browser.
 *  - Editor: the canvas provides `cmsPostPreview` — { post } with the site's latest
 *    published post, or the one the designer picked — and the blocks read that.
 *  - Neither: SAMPLE_POST, so a template never renders empty.
 */
import { firstLabel } from '../postListBlock/postListShared.js';
import { wpSizedWidth } from './imageSrc.js';

export var SAMPLE_POST = {
	websitePostID: null,
	websitePostTitle: 'Your headline goes here: the post title fills this in',
	websitePostExcerpt: 'The summary of the article appears here. It is the excerpt of the post the template is applied to.',
	websitePostDate: '2026-01-15 09:00:00',
	websitePostFeatureImageUrl: '',
	websitePostFeatureImageUrlLarge: '',
	websitePostFeatureImageUrlFull: '',
	featureImageCredit: null,
	websitePostUrl: '#',
	readingTime: 4,
	authors: [{ label: 'Author Name', value: 'author-name' }],
	categories: [{ label: 'Category', value: 'category' }],
	tags: [],
	isSample: true
};

/**
 * The post a block should show: its own filled prop, else the editor preview, else the sample.
 * @param {Object|null} ownProp
 * @param {{post: Object}|null} preview
 * @returns {Object}
 */
export function resolvePost(ownProp, preview) {
	if (ownProp && typeof ownProp === 'object' && ownProp.websitePostTitle !== undefined) return ownProp;
	if (preview && preview.post && preview.post.websitePostTitle !== undefined) return preview.post;
	return SAMPLE_POST;
}

/** @returns {{label: string, slug: string, url: string}|null} */
export function postCategory(post) {
	var c = post && Array.isArray(post.categories) && post.categories[0];
	if (!c) return null;
	var slug = c.slug || c.taxonomySlug || c.value || '';
	return { label: firstLabel(post.categories), slug: slug, url: slug ? '/category/' + slug : '' };
}

/** @returns {{label: string, slug: string, url: string}|null} */
export function postAuthor(post) {
	var a = post && Array.isArray(post.authors) && post.authors[0];
	if (!a) return null;
	var slug = a.slug || a.taxonomySlug || a.value || '';
	return { label: firstLabel(post.authors), slug: slug, url: slug ? '/author/' + slug : '' };
}

var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The post date as text. 'long' → November 4, 2026 · 'short' → Nov 4, 2026 · 'numeric' → 11/4/2026.
 * The stored value is a MySQL datetime; it is read as a calendar date so time zones never shift the day.
 */
export function postDateLabel(post, format) {
	var raw = post && post.websitePostDate ? String(post.websitePostDate) : '';
	var m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return '';
	var y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
	if (!mo || !d) return '';
	if (format === 'numeric') return mo + '/' + d + '/' + y;
	var month = MONTHS[mo - 1];
	if (format === 'short') month = month.slice(0, 3);
	return month + ' ' + d + ', ' + y;
}

/** Minutes to read, from the server figure or a word count of the body when given. */
export function postReadingTime(post) {
	var n = post && parseInt(post.readingTime, 10);
	return n && n > 0 ? n : 0;
}

/**
 * The best picture sources for the post: WordPress-synced posts keep the medium copy in the base
 * column and the large/full copies beside it (see postListBlock pic()).
 * @returns {{src: string, large: string, full: string}}
 */
export function postImage(post) {
	var base = (post && post.websitePostFeatureImageUrl) || '';
	var large = (post && post.websitePostFeatureImageUrlLarge) || '';
	var full = (post && post.websitePostFeatureImageUrlFull) || '';
	return { src: full || large || base, large: large || base, full: full || large || base, base: base };
}

/**
 * Ready-made copies of a remote (WordPress) picture, labelled by the width in their file
 * names, for imgAttrs({ candidates }). Pictures on the media proxy get real variants instead.
 * @returns {Array<{url: string, w: number}>}
 */
export function pictureCandidates(post) {
	var img = postImage(post);
	var largeW = img.large && img.large !== img.base ? wpSizedWidth(img.large, 1024) : 0;
	return [
		img.base ? { url: img.base, w: wpSizedWidth(img.base, 300) } : null,
		img.large && img.large !== img.base ? { url: img.large, w: largeW } : null,
		img.full && img.full !== img.large ? { url: img.full, w: wpSizedWidth(img.full, Math.max(2048, largeW * 2)) } : null
	].filter(Boolean);
}
